import { useMemo } from 'react';
import { Field, useFormikContext } from 'formik';
import cronstrue from 'cronstrue';
import { stringToArray } from 'cron-converter';
import { Autocomplete } from '../../../input/Autocomplete/Autocomplete';
import {
  CustomOptionType,
  EligibleFrequencyUnitsInCron,
  SyncFrequencyBannerType,
  SyncFrequencyTypeFeatureOption,
  SyncFrequencyTypes
} from '../../interfaces/pipeline-settings-config';
import styles from './SyncFrequencyWithCronFormField.module.scss';
import { frequencyTypeFieldMap } from './utils';
import { PipelineSettingsFormFields } from '../../PipelineSettings/interfaces/pipeline-settings-form-state';
import { Banner } from '../../../banner/Banner';
import { convertMillisToNearestUnits, getDurationString } from '../../../shared/utils/date';
import EntityBanner from '../../../shared/components/EntityBanner/EntityBanner';
import { DurationUnit } from '../../../shared/interfaces/date';
import { DocLink } from '../../../docs/DocLink/DocLink';
import { useAnalyticsTracker } from '../../../tracking/hooks/useAnalyticsTracker';
import { PipelineSyncFrequencyTrackers } from '../../tracking/pipeline-sync-frequency-trackers';
import { SyncFrequencyOption } from '../../../shared/interfaces/integration';

export interface SyncFrequencyWithCronFormFieldProps {
  disabled?: boolean;
  className?: string;
  customFieldClassName?: string;
  fieldName: string;
  featureOptions: FeatureOptions;
}

export interface FeatureOptions {
  options: SyncFrequencyTypeFeatureOption[];
  optionsForMins: SyncFrequencyOption[];
  custom_option: { [key in SyncFrequencyTypes]: CustomOptionType | undefined };
  banner: SyncFrequencyBannerType | null;
}

export function SyncFrequencyWithCronFormField({
  disabled,
  className,
  fieldName,
  featureOptions,
  customFieldClassName
}: SyncFrequencyWithCronFormFieldProps) {
  const { eventTrack } = useAnalyticsTracker();

  const { values, setFieldValue, errors } = useFormikContext<{
    [PipelineSettingsFormFields.SYNC_FREQUENCY_TYPE]: {
      unit: EligibleFrequencyUnitsInCron;
      value: SyncFrequencyTypes;
    };
    [PipelineSettingsFormFields.SYNC_FREQUENCY]: number;
    [PipelineSettingsFormFields.SYNC_FREQUENCY_IN_CRON]: string;
  }>();

  const { value: syncFrequencyType, unit: syncFrequencyTypeUnit } =
    values[PipelineSettingsFormFields.SYNC_FREQUENCY_TYPE];

  const syncFrequencyInMs = values[PipelineSettingsFormFields.SYNC_FREQUENCY];
  const syncFrequencyInCron = values[PipelineSettingsFormFields.SYNC_FREQUENCY_IN_CRON];

  /* between hours text field and mins dropdown,
    if the value format of one another doesnt match,
    set to default value:
    MINUTES: options[0],
    Hours: 1

  */
  const setDefaultValueForUnitType = (
    unit: EligibleFrequencyUnitsInCron,
    existingValue: number
  ) => {
    const valueFoundInOptions = featureOptions.optionsForMins.findIndex(
      option => option.value === existingValue
    );
    const valueWithUnits = convertMillisToNearestUnits(existingValue);

    if (unit === EligibleFrequencyUnitsInCron.MINUTES && valueFoundInOptions === -1) {
      setFieldValue(
        PipelineSettingsFormFields.SYNC_FREQUENCY,
        featureOptions.optionsForMins[0].value
      );
    } else if (
      unit === EligibleFrequencyUnitsInCron.HOURS &&
      valueWithUnits.unit !== DurationUnit.HOURS &&
      valueWithUnits.unit !== DurationUnit.DAYS
    ) {
      setFieldValue(
        PipelineSettingsFormFields.SYNC_FREQUENCY,
        60 * 60 * 1000 // 1hr
      );
    }
  };

  const frequencyTypeFieldProps = useMemo(() => {
    eventTrack({
      action:
        PipelineSyncFrequencyTrackers.PIPELINE_TRACKER_PIPELINE_SETTINGS_SYNC_FREQUENCY_TYPE_SELECTED,
      properties: {
        frequency_type: syncFrequencyTypeUnit
      }
    });
    const fieldProps = frequencyTypeFieldMap[syncFrequencyTypeUnit];

    if (syncFrequencyTypeUnit === EligibleFrequencyUnitsInCron.MINUTES) {
      fieldProps.fieldProps = { ...fieldProps.fieldProps, options: featureOptions.optionsForMins };
    }

    if (syncFrequencyTypeUnit !== EligibleFrequencyUnitsInCron.CRON) {
      setDefaultValueForUnitType(
        syncFrequencyTypeUnit,
        values[PipelineSettingsFormFields.SYNC_FREQUENCY]
      );
    }
    return fieldProps;
  }, [syncFrequencyTypeUnit]);

  const frequency = useMemo(() => {
    try {
      if (
        syncFrequencyInCron &&
        syncFrequencyType === SyncFrequencyTypes.CRON &&
        stringToArray(syncFrequencyInCron)
      ) {
        return cronstrue.toString(syncFrequencyInCron);
      }

      if (syncFrequencyInMs && syncFrequencyType === SyncFrequencyTypes.FIXED) {
        return `Every ${getDurationString(syncFrequencyInMs, false, true)}`;
      }

      return undefined;
    } catch (error) {
      return undefined;
    }
  }, [syncFrequencyType, syncFrequencyInCron, syncFrequencyInMs]);

  const getHelperTextForField = useMemo(() => {
    const customOption = featureOptions.custom_option[syncFrequencyType];
    if (!customOption) {
      return undefined;
    }

    if (customOption.time_unit !== DurationUnit.MILLISECONDS) {
      return (
        <span className={styles.hourTextFieldHelperText}>{`Specify sync frequency in range of ${
          customOption.min
        } to ${customOption.max} ${customOption.time_unit.toLowerCase()}`}</span>
      );
    }

    let minWithUnits = convertMillisToNearestUnits(customOption!.min);
    const maxWithUnits = convertMillisToNearestUnits(customOption!.max);

    if (
      syncFrequencyTypeUnit === EligibleFrequencyUnitsInCron.HOURS &&
      minWithUnits.unit === DurationUnit.MINUTES
    ) {
      minWithUnits = { value: 1, formattedUnit: 'hour', unit: DurationUnit.HOURS };
    }

    if (syncFrequencyTypeUnit !== EligibleFrequencyUnitsInCron.CRON) {
      return (
        <span
          className={styles.hourTextFieldHelperText}
        >{`Specify sync frequency in range of ${minWithUnits.value} ${minWithUnits.formattedUnit} to ${maxWithUnits.value} ${maxWithUnits.formattedUnit}`}</span>
      );
    }
    return (
      <span className={styles.hourTextFieldHelperText}>
        To know how to use CRON
        <DocLink className='ml-1' dataId='' docLink='' section='' />
      </span>
    );
  }, [syncFrequencyType, syncFrequencyInCron, syncFrequencyInMs]);

  const hasErrors =
    errors[PipelineSettingsFormFields.SYNC_FREQUENCY_IN_CRON] ||
    errors[PipelineSettingsFormFields.SYNC_FREQUENCY];

  return (
    <div className={className}>
      <div className={styles.syncFrequencyContainer}>
        <div className={styles.syncFrequencyRow}>
          <div className='text-body-2 d-flex align-items-center'>Frequency Type</div>

          <div>
            <Autocomplete
              options={featureOptions.options}
              value={values[PipelineSettingsFormFields.SYNC_FREQUENCY_TYPE]}
              dataId='frequency-type'
              size='medium'
              disabled={disabled}
              disableClearable
              onChange={(_, value) => {
                setFieldValue(PipelineSettingsFormFields.SYNC_FREQUENCY_TYPE, value);
              }}
            />
          </div>
        </div>

        {frequencyTypeFieldProps ? (
          <div className={styles.syncFrequencyRow}>
            <div className='text-body-2 d-flex align-items-center'>
              {frequencyTypeFieldProps.fieldTitle}
            </div>

            <div>
              <Field
                name={fieldName}
                disabled={disabled}
                className={customFieldClassName}
                {...frequencyTypeFieldProps.fieldProps}
                helperText={getHelperTextForField}
              />
            </div>
          </div>
        ) : null}
      </div>

      {frequency && !hasErrors ? (
        <div>
          <Banner
            open
            dataId='frequency-sync'
            color='primary'
            icon='time'
            className='text-primary mt-5'
          >
            Hevo will sync your Pipeline <span className='text-bold'>{frequency}</span>
          </Banner>
        </div>
      ) : null}

      {featureOptions.banner ? (
        <EntityBanner
          className='mt-3'
          dataId='sync-freq-cron'
          content={featureOptions.banner?.text}
          type={featureOptions.banner?.type}
        />
      ) : null}
    </div>
  );
}
